import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { useHistory } from "react-router-dom";

// Actions
import { resetTgGroup } from "../../../actions/org/OrgTargetGroupActions";
import {
  getCampaignPlan,
  resetCampaignPlanDetails,
} from "../../../actions/campaign-plan/CampaignPlanActions";
import {
  getBasicCampaignDetails,
  resetCampaignDetails,
} from "../../../actions/campaign/CampaignBasicInfoActions";
import { resetSellerMediaByStretchIds } from "../../../actions/seller/SellerMediaActions";
import {
  clearCampaignMedia,
  getCampaignMedia,
} from "../../actions/media-selection/MediaSelectionActions";
import {
  getRoadStretchesByCityId,
  resetCityRoadStretches,
} from "../../actions/campaign-planning/CampaignPlanningRoadStretchesActions";

// Utils and constants
import {
  getCitySpecificMediaList,
  getSelectedStretchesAndMediaCount,
  getSitesTotalImpressionsAndCost,
  getStaticAndDoohSitesCount,
} from "./MediaContainerUtil";
import {
  getDigitalMediasSpotCount,
  getStretchesTotalOtsSplit,
  getTotalOtsSplitForStretchesBasedOnSelectedMedias,
} from "../../utils/campaignPlanningUtil";
import { defaultPagination } from "../../../constants/UrlConstants";
import { getTotalReachOfSelectedMediasStretches } from "../../utils/ReachUtils";

// Components
import Spinner from "../../../components/spinner/Spinner";
import PageHeader from "../../../mavin/components/page-header/PageHeader";
import FiltersAndSelectionChipsCard from "../components/FiltersAndSelectionChipsCard";
import SelectionOverviewCard from "../components/SelectionOverviewCard";
import { continueToPlanning } from "../components/CampaignRedirect";
import {
  ContinueEditing,
  SelectionReviewCampaign,
  SaveAsDraft,
} from "../components/HeaderNav";
import RoadAndMediaSection from "./RoadAndMediaSection";
import CreateMediaForm from "./CreateMediaForm";
import MediaImagesViewForm from "./MediaImagesViewForm";

// Page Components
function MediaSelectionHeader({
  campaign = {},
  cityId,
  cityCampaignPlan = {},
}) {
  const history = useHistory();
  const { id, title = "" } = campaign;

  // for DOOH only campaign
  const { isDigital } = cityCampaignPlan || {};

  const pageActions = (
    <div className="d-flex">
      <ContinueEditing
        onClickAction={() => continueToPlanning(id, cityId, history, isDigital)}
      />
      <SaveAsDraft />
      <SelectionReviewCampaign campaignId={id} cityId={cityId} />
    </div>
  );

  return <PageHeader title={title} actions={pageActions} shadow={true} />;
}

// Stretch Selection Overview
function StretchSelectionOverview({ campaign = {}, cityPlan = {} }) {
  // stretch details map (overall stretch data including all medias)
  const roadStretchDetailsMap = useSelector(
    (state) => state.planningRoadStretches.roadStretchDetailsMap
  );

  // city plan
  const { roadStretchOtsMap = {} } = cityPlan || {};

  // selected stretch data and total mediaCount on stretch
  const { totalStretchesCount, totalMediaCount } =
    getSelectedStretchesAndMediaCount(cityPlan, roadStretchDetailsMap);

  // total ots values
  const { ots = 0, targetOts = 0 } =
    getStretchesTotalOtsSplit(roadStretchOtsMap);

  // overview data
  const stretchesOverviewData = [
    {
      title: "Stretches",
      value: totalStretchesCount,
    },
    {
      title: "Total Sites",
      value: totalMediaCount,
    },
    {
      title: "Total Impressions",
      value: ots,
    },
    {
      title: "TG Impressions",
      value: targetOts,
    },
  ];

  return (
    <div className="row align-items-stretch">
      <div className="col-12 col-xl-6">
        <h5>Stretch Selection Overview</h5>
        <SelectionOverviewCard data={stretchesOverviewData} />
      </div>

      {/* filters card */}
      <FiltersAndSelectionChipsCard campaign={campaign} cityPlan={cityPlan} />
    </div>
  );
}

// Site Selection Overview
function SiteSelectionOverview({ cityPlan = {}, campaignMedias = [] }) {
  const { cityId } = useParams();

  // stretch details map (overall stretch data including all medias)
  const roadStretchDetailsMap = useSelector(
    (state) => state.planningRoadStretches.roadStretchDetailsMap
  );

  // selected stretch data and total mediaCount on stretch
  const { selectedStretchesMap, totalMediaCount } =
    getSelectedStretchesAndMediaCount(cityPlan, roadStretchDetailsMap);

  // city specific medias
  const citySpecificMediaList = getCitySpecificMediaList(
    campaignMedias,
    cityId
  );
  const {
    static: staticSitesCount = 0,
    DOOH: doohSitesCount = 0,
    total: totalSelectedMediaCount = 0,
  } = getStaticAndDoohSitesCount(citySpecificMediaList);

  // city plan data (for pure dooh campaigns)
  const { isDigital = false } = cityPlan || {};

  // spot count
  const digitalMediaSpotCounts = getDigitalMediasSpotCount(
    citySpecificMediaList
  );

  // sites card data
  const sitesCardData = [
    {
      title: "Total",
      value: totalMediaCount,
    },
    {
      title: "Selected",
      value: totalSelectedMediaCount,
    },
    {
      title: isDigital ? "Spots" : "Static",
      value: isDigital ? digitalMediaSpotCounts : staticSitesCount,
    },
    {
      title: "DOOH",
      value: doohSitesCount,
    },
  ];

  // total impressions and cost
  const { totalEstCost = 0 } = getSitesTotalImpressionsAndCost(
    citySpecificMediaList
  );

  // city plan data
  const { roadStretchOtsMap = {} } = cityPlan || {};

  // Total OtsSplit For Stretches Based On Selected Medias
  const { ots = 0, targetOts = 0 } =
    getTotalOtsSplitForStretchesBasedOnSelectedMedias(
      roadStretchOtsMap,
      selectedStretchesMap,
      citySpecificMediaList
    );

  // total reach for selected medias stretches
  const totalReach = getTotalReachOfSelectedMediasStretches(
    cityPlan,
    citySpecificMediaList,
    roadStretchDetailsMap
  );

  // ots & cost card data
  const otsAndCostCardData = [
    {
      title: "Total Impressions",
      value: ots,
    },
    {
      title: "TG Impressions",
      value: targetOts,
    },
    {
      title: "Total Reach",
      value: totalReach,
    },
    {
      title: "Estimated Cost",
      value: totalEstCost,
    },
  ];

  return (
    <>
      <h5 className="mt-5">Site Selection Overview</h5>
      <div className="row align-items-stretch">
        <div className="col-6">
          <SelectionOverviewCard data={sitesCardData} />
        </div>
        <div className="col-6">
          <SelectionOverviewCard data={otsAndCostCardData} />
        </div>
      </div>
    </>
  );
}

/**
 * Page
 */
export default function MediaSelectionPage() {
  const dispatch = useDispatch();
  const { campaignId, cityId } = useParams();

  // Get campaignPlan
  const cityCampaignPlan =
    useSelector((state) => state.campaignPlan.campaignPlan) || {};

  // Get campaign
  const campaign = useSelector((state) => state.c_campaignBasicInfo.campaign);

  //open media modal
  const openCreateMediaModal = useSelector(
    (state) => state.createMediaModal.openModal
  );

  // CampaignMedias
  const campaignMediasMap = useSelector(
    (state) => state.mediaSelection.mediaIdToCampaignMedia
  );
  const campaignMedias = Object.values(campaignMediasMap);

  // open View Media Images Modal
  const openViewMediaImagesModal = useSelector(
    (state) => state.mediaSelection.openViewMediaImagesModal
  );

  // Api Loadings
  const campaignPlanLoading = useSelector(
    (state) => state.campaignPlan.campaignPlanLoading
  );
  const campaignBasicInfoLoading = useSelector(
    (state) => state.c_campaignBasicInfo.campaignLoading
  );
  const roadStretchesLoading = useSelector(
    (state) => state.planningRoadStretches.loading
  );
  const campaignMediasLoading = useSelector(
    (state) => state.mediaSelection.loadingCampaignMedia
  );

  const isPageLoading =
    campaignPlanLoading ||
    campaignBasicInfoLoading ||
    roadStretchesLoading ||
    campaignMediasLoading;

  const {
    roadStretchOtsMap = {},
    mediaTypes = [],
    isDigital = false,
  } = cityCampaignPlan || {};
  const planStretchIds = Object.keys(roadStretchOtsMap);
  const planStretchIdsLength = planStretchIds.length;

  // pagination constants
  const { pageNo, pageSizeMax } = defaultPagination || {};

  useEffect(() => {
    // Reset Actions
    dispatch(resetCityRoadStretches());
    dispatch(resetSellerMediaByStretchIds());
    dispatch(resetCampaignPlanDetails());
    dispatch(resetCampaignDetails());
    dispatch(resetTgGroup());
    dispatch(clearCampaignMedia());

    // Get Data Actions
    dispatch(getCampaignPlan(campaignId, cityId));
    dispatch(getBasicCampaignDetails(campaignId));
    dispatch(getCampaignMedia(campaignId, pageNo, pageSizeMax));
  }, [dispatch, campaignId, cityId]);

  // getting city road stretches
  useEffect(() => {
    if (planStretchIdsLength > 0) {
      dispatch(getRoadStretchesByCityId(cityId, "", isDigital, [], mediaTypes));
    }
  }, [dispatch, campaignId, planStretchIdsLength]);

  // page loading
  if (isPageLoading) {
    return <Spinner className="spinner-center mt-2" />;
  }

  return (
    <div className="content-wrapper media-selection">
      <MediaSelectionHeader
        campaign={campaign}
        cityId={cityId}
        cityCampaignPlan={cityCampaignPlan}
      />

      <div className="page-content">
        <StretchSelectionOverview
          campaign={campaign}
          cityPlan={cityCampaignPlan}
        />

        <SiteSelectionOverview
          cityPlan={cityCampaignPlan}
          campaignMedias={campaignMedias}
        />

        {/* Stretch And Media Selection Table */}
        <RoadAndMediaSection
          cityPlan={cityCampaignPlan}
          campaignMediasMap={campaignMediasMap}
        />
      </div>

      {/* MODAL */}
      {openCreateMediaModal && (
        <CreateMediaForm campaignId={campaignId} cityId={cityId} />
      )}

      {/* view media images */}
      {openViewMediaImagesModal && <MediaImagesViewForm />}
    </div>
  );
}
